import { ALLOWED_LANGUAGE_CODES } from '../common/i18n/Text.ts';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import * as SettingsState from '../common/state/SettingsState.ts';
import { Store, useStore } from 'dg-web-shared/lib/Flux.tsx';
import { envIsProduction } from 'dg-web-shared/lib/Environment.ts';
import { SystemStatus } from '../common/components/SystemStatusLink.tsx';
import { css } from '@emotion/css';
import { ColorHex } from '../ui/Colors.ts';
import { OperatorTypo } from '../ui/OperatorTypo.ts';

import parkingPortalLogo from '../../assets/parkingportal_logo_white.svg';
import { TermOfUseLink } from './TermOfUseFiles.tsx';
import bg from './bg_landingpage.jpg';
import { Link as RouterLink, Outlet, useNavigate } from 'react-router';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { ParkingPortalLogoHeader } from '../mobile/layout/ParkingPortalLogoHeader.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { TurnstileAction } from 'dg-web-shared/lib/TurnstileActions';
import * as AuthenticationActions from '../app/actions/AuthenticationActions.ts';
import * as AuthenticationState from '../app/state/AuthenticationState.ts';
import { LoginState } from './LandingPageState.ts';
import * as Flux from 'dg-web-shared/lib/Flux.tsx';
import { Language, languageFromString } from 'dg-web-shared/lib/Text.ts';
import { useEffect, useRef, useState } from 'react';

export function LandingPage() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minWidth: '748px',
            }}
        >
            <NavBar />
            <LoginBlock />
            <Outlet />
        </Box>
    );
}

function NavBar() {
    const { language } = useLanguage();
    return (
        <div
            className={css({
                height: '48px',
                background: envIsProduction()
                    ? ColorHex.navbar
                    : ColorHex.fPendent,
                display: 'flex',
                justifyContent: 'space-between',
            })}
        >
            <img
                src={parkingPortalLogo}
                className={css({
                    width: '136px',
                    height: '48px',
                    marginLeft: '15px',
                })}
            />
            <div
                className={css({
                    display: 'flex',
                    alignItems: 'center',
                })}
            >
                <div className={css({ marginRight: '32px' })}>
                    <TermOfUseLink lang={language} />
                </div>

                <div className={css({ marginRight: '48px' })}>
                    <SystemStatus />
                </div>

                <LanguageChooser />
            </div>
        </div>
    );
}

function LanguageChooser() {
    const { storeState, update } = useStore(store => ({
        settings: new SettingsState.StateSlice(store).state,
    }));
    return (
        <div
            className={css({
                display: 'flex',
                alignItems: 'center',
                marginRight: '48px',
            })}
        >
            {ALLOWED_LANGUAGE_CODES.map(lang =>
                lang !== storeState.settings.language ? (
                    <Clickable
                        element="div"
                        key={lang}
                        className={css({
                            ...OperatorTypo.bodyC,
                            color: ColorHex.white,
                            padding: '10px',
                            '&:hover': {
                                color: ColorHex.lightblue,
                            },
                        })}
                        onClick={() =>
                            update((store: Store) => {
                                new SettingsState.StateSlice(
                                    store,
                                ).writeLanguage(lang);
                                return 'setLanguage';
                            })
                        }
                    >
                        {lang}
                    </Clickable>
                ) : (
                    <div
                        key={lang}
                        className={css({
                            ...OperatorTypo.bodyC,
                            color: ColorHex.white,
                            padding: '10px',
                            cursor: 'default',
                            backgroundColor: ColorHex.navbarhover,
                        })}
                    >
                        {lang}
                    </div>
                ),
            )}
        </div>
    );
}

function setUsername(store: Flux.Store, username: string) {
    LoginState.stateWrite(store, {
        username: username,
        showCredentialsError: false,
        showTurnstileError: false,
    });
    return 'setUsername';
}

function setPassword(store: Flux.Store, password: string) {
    LoginState.stateWrite(store, {
        password: password,
        showCredentialsError: false,
        showTurnstileError: false,
    });
    return 'setPassword';
}

function setRememberMe(store: Flux.Store, rememberMe: boolean) {
    LoginState.stateWrite(store, {
        longSession: rememberMe,
    });
    return 'setRememberMe';
}

const PARKINGPORTAL_TURNSTILE_SITE_KEY = '0x4AAAAAABatiRYriPvAMuT9';

function LoginBlock() {
    const navigate = useNavigate();
    const { storeState, update } = useStore(s => ({
        authentication: new AuthenticationState.StateSlice(s).state,
        login: LoginState.get(s),
        settings: new SettingsState.StateSlice(s).state,
    }));
    const turnstileRef = useRef<TurnstileInstance | null>(null);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const showLoginError =
        storeState.login.showCredentialsError ||
        storeState.login.showTurnstileError;

    useEffect(() => {
        if (!showLoginError) {
            return;
        }

        setTurnstileToken(null);
        turnstileRef.current?.reset();
    }, [showLoginError]);

    return (
        <DesktopLoginBackground>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '551px',
                    minHeight: '700px',
                    alignSelf: 'center',
                    overflowX: 'hidden',
                }}
            >
                <ParkingPortalLogoHeader />
                <Box
                    sx={{
                        padding: 3,
                        flexGrow: 1,
                        backgroundColor: ColorHex.white,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <TextField
                        label={
                            <Localized
                                de="Benutzername"
                                fr="Nom utilisateur"
                                it="Nome utente"
                                en="Username"
                            />
                        }
                        value={storeState.login.username}
                        onChange={v =>
                            update(store => setUsername(store, v.target.value))
                        }
                        fullWidth={true}
                        margin="normal"
                    />
                    <TextField
                        label={
                            <Localized
                                de="Passwort"
                                fr="Mot de passe"
                                it="Password"
                                en="Password"
                            />
                        }
                        value={storeState.login.password}
                        onChange={v =>
                            update(store => setPassword(store, v.target.value))
                        }
                        fullWidth={true}
                        type="password"
                        error={storeState.login.showCredentialsError}
                        helperText={
                            storeState.login.showCredentialsError ? (
                                <Localized
                                    de="Benutzername oder Passwort sind ungültig."
                                    fr="Le nom d'utilisateur ou le mot de passe ne sont valables."
                                    it="Nome utente o password non validi."
                                    en="Invalid username or password."
                                />
                            ) : undefined
                        }
                        autoComplete="current-password"
                    />
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            flexDirection: 'column',
                            flexGrow: 1,
                            gap: 2,
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: 320, mt: 1 }}>
                            <Turnstile
                                ref={turnstileRef}
                                siteKey={PARKINGPORTAL_TURNSTILE_SITE_KEY}
                                options={{
                                    theme: 'light',
                                    size: 'flexible',
                                    action: TurnstileAction.OPERATOR_LOGIN,
                                }}
                                onError={() => setTurnstileToken(null)}
                                onExpire={() => setTurnstileToken(null)}
                                onSuccess={token => setTurnstileToken(token)}
                            />
                        </Box>
                        {storeState.login.showTurnstileError && (
                            <Typography
                                component="div"
                                color="error"
                                sx={{ textAlign: 'center' }}
                            >
                                <Localized
                                    de="Bitte bestätigen Sie, dass Sie kein Roboter sind."
                                    fr="Veuillez confirmer que vous n'êtes pas un robot."
                                    it="Confermate di non essere un robot."
                                    en="Please confirm that you are not a robot."
                                />
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={
                                !storeState.login.username ||
                                !storeState.login.password ||
                                storeState.login.showCredentialsError ||
                                turnstileToken == null
                            }
                            loading={storeState.authentication.statusPending}
                            onClick={() =>
                                update(AuthenticationActions.login(navigate), {
                                    username:
                                        storeState.login.username?.trim() || '',
                                    password: storeState.login.password || '',
                                    longSession: storeState.login.longSession,
                                    turnstileToken,
                                })
                            }
                            sx={{ marginBottom: 5 }}
                        >
                            <Localized
                                de="Anmelden"
                                fr="Connexion"
                                it="Accesso"
                                en="Login"
                            />
                        </Button>
                        <Link
                            component={RouterLink}
                            sx={{
                                color: theme => theme.palette.primary.main,
                                fontWeight: 700,
                            }}
                            underline="hover"
                            to={resetPasswordLink(
                                languageFromString(
                                    storeState.settings.language,
                                ),
                                storeState.login.username || '',
                            )}
                        >
                            <Localized
                                de="Passwort vergessen?"
                                fr="Mot de passe oublié?"
                                it="Password dimenticata?"
                                en="Forgot password?"
                            />
                        </Link>
                        <FormControlLabel
                            labelPlacement="end"
                            control={
                                <Checkbox
                                    color="primary"
                                    disableRipple
                                    disableFocusRipple
                                    disableTouchRipple
                                />
                            }
                            label={
                                <Typography
                                    component="span"
                                    sx={{
                                        color: theme =>
                                            theme.palette.primary.main,
                                        fontWeight: 700,
                                    }}
                                >
                                    <Localized
                                        de="Login speichern"
                                        fr="Rester connecté"
                                        it="Rimani connesso"
                                        en="Stay signed in"
                                    />
                                </Typography>
                            }
                            checked={storeState.login.longSession}
                            onChange={() =>
                                update(store =>
                                    setRememberMe(
                                        store,
                                        !storeState.login.longSession,
                                    ),
                                )
                            }
                        />
                    </Box>
                </Box>
                <Box
                    sx={theme => ({
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                        height: 54,
                        flexShrink: 0,
                    })}
                >
                    <Link
                        sx={{
                            color: 'common.white',
                            '&:hover': {
                                color: 'common.white',
                            },
                        }}
                        underline="hover"
                        href={'mailto:info@parkingportal.ch'}
                    >
                        info@parkingportal.ch
                    </Link>
                </Box>
            </Box>
        </DesktopLoginBackground>
    );
}

export function DesktopLoginBackground({
    children,
}: {
    children: React.ReactNode;
}) {
    return (
        <div
            className={css({
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                overflowX: 'hidden',
                overflowY: 'auto',
                backgroundSize: 'cover',
                backgroundColor: ColorHex.black,
                backgroundImage: `url(${bg})`,
            })}
        >
            {children}
        </div>
    );
}

function resetPasswordLink(language: Language, username: string): string {
    return (
        '/reset-password?username=' +
        encodeURIComponent(username) +
        '&language=' +
        encodeURIComponent(language)
    );
}
